import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'

const JURISDICTIONS = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT']
const WORKPLACE_TYPES = ['office', 'warehouse', 'field', 'construction', 'manufacturing']
const MATURITY_LEVELS = [
  { id: 'reactive', label: 'Reactive', desc: 'We respond to incidents after they happen' },
  { id: 'compliance', label: 'Compliance', desc: 'We follow regulations and do what\'s required' },
  { id: 'proactive', label: 'Proactive', desc: 'We prevent incidents before they happen' },
  { id: 'generative', label: 'Generative', desc: 'Safety is embedded in everything we do' },
]

export default function SetupWizard() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [form, setForm] = useState({
    primary_jurisdiction: 'NSW',
    additional_jurisdictions: [] as string[],
    workplace_types: [] as string[],
    safety_maturity: 'compliance',
    growth_mode: 'stable',
    shift_work: false,
    union_presence: false,
    safety_strategy: '',
  })
  const [aiInput, setAiInput] = useState('')
  const [aiLoading, setAiLoading] = useState(false)
  const [aiError, setAiError] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  const runAI = async () => {
    if (!aiInput) return
    setAiLoading(true)
    setAiError('')
    try {
      const { data } = await api.post('/organisation/setup-wizard', { natural_language_input: aiInput })
      setForm(f => ({
        ...f,
        primary_jurisdiction: data.primary_jurisdiction || f.primary_jurisdiction,
        workplace_types: data.workplace_types || f.workplace_types,
        safety_maturity: data.safety_maturity || f.safety_maturity,
        growth_mode: data.growth_mode || f.growth_mode,
        shift_work: data.shift_work ?? f.shift_work,
        union_presence: data.union_presence ?? f.union_presence,
        safety_strategy: data.safety_strategy || f.safety_strategy,
      }))
      setStep(2)
    } catch (err: any) {
      setAiError(err?.response?.data?.message || err?.response?.data?.error || 'Aria could not process your request. Please try again or configure manually.')
    }
    setAiLoading(false)
  }

  const save = async () => {
    setSaving(true)
    setSaveError('')
    try {
      await api.put('/organisation/profile', form)
      navigate('/dashboard')
    } catch (err: any) {
      setSaveError(err?.response?.data?.error || 'Failed to save configuration. Please try again.')
      setSaving(false)
    }
  }

  const toggle = (arr: string[], val: string) => arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val]

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'var(--bg)' }}>
      <div className="w-full max-w-2xl space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text)' }}>Welcome to HSEQ Intelligence</h1>
          <p className="mt-2" style={{ color: 'var(--text-muted)' }}>Let's set up your organisation. Step {step} of 3.</p>
        </div>

        {step === 1 && (
          <div className="card space-y-4">
            <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Tell Aria about your organisation</h2>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              Describe your business in plain English — Aria will pre-fill your configuration. You'll review everything before saving.
            </p>
            <textarea
              rows={5}
              value={aiInput}
              onChange={e => setAiInput(e.target.value)}
              placeholder="e.g. We're a mid-size construction company in NSW with about 80 employees, mainly operating on commercial building sites. We work in Brisbane sometimes too. We do a lot of work at heights and operate cranes. We're compliance focused but want to improve. We have two shifts..."
            />
            <div className="ai-label text-sm">AI-assisted — all suggestions require your review and approval before being applied</div>
            {aiError && <p className="text-sm" style={{ color: 'var(--danger)' }}>{aiError}</p>}
            <div className="flex gap-3">
              <button onClick={runAI} disabled={aiLoading || !aiInput} className="btn btn-primary">
                {aiLoading ? 'Aria is thinking...' : 'Configure with AI'}
              </button>
              <button onClick={() => setStep(2)} className="btn btn-secondary">Configure manually</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="card space-y-6">
            <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Review & Confirm Configuration</h2>

            <div>
              <label>Primary Jurisdiction *</label>
              <select value={form.primary_jurisdiction} onChange={e => setForm(f => ({ ...f, primary_jurisdiction: e.target.value }))}>
                {JURISDICTIONS.map(j => <option key={j} value={j}>{j}</option>)}
              </select>
            </div>

            <div>
              <label>Workplace Types</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {WORKPLACE_TYPES.map(t => (
                  <button key={t} type="button" onClick={() => setForm(f => ({ ...f, workplace_types: toggle(f.workplace_types, t) }))}
                    className={`badge cursor-pointer capitalize ${form.workplace_types.includes(t) ? 'badge-low' : ''}`}
                    style={!form.workplace_types.includes(t) ? { background: 'var(--bg)', color: 'var(--text-secondary)', border: '1px solid var(--border)' } : {}}>
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label>Safety Maturity Level</label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {MATURITY_LEVELS.map(m => (
                  <button key={m.id} type="button" onClick={() => setForm(f => ({ ...f, safety_maturity: m.id }))}
                    className={`text-left p-3 rounded-lg border transition-all ${form.safety_maturity === m.id ? 'border-accent' : ''}`}
                    style={form.safety_maturity === m.id ? { borderColor: 'var(--accent)', background: 'var(--accent-light)' } : { borderColor: 'var(--border)' }}>
                    <p className="font-semibold text-sm" style={{ color: form.safety_maturity === m.id ? 'var(--accent)' : 'var(--text)' }}>{m.label}</p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{m.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label>Safety Strategy <span className="font-normal text-xs" style={{ color: 'var(--text-muted)' }}>(optional — helps AI agents give better advice)</span></label>
              <textarea rows={3} value={form.safety_strategy} onChange={e => setForm(f => ({ ...f, safety_strategy: e.target.value }))} placeholder="Describe your safety goals and strategy..." />
            </div>

            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer m-0 font-normal">
                <input type="checkbox" checked={form.shift_work} onChange={e => setForm(f => ({ ...f, shift_work: e.target.checked }))} className="w-4 h-4" style={{ width: '1rem' }} />
                <span className="text-sm" style={{ color: 'var(--text)' }}>Shift work</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer m-0 font-normal">
                <input type="checkbox" checked={form.union_presence} onChange={e => setForm(f => ({ ...f, union_presence: e.target.checked }))} className="w-4 h-4" style={{ width: '1rem' }} />
                <span className="text-sm" style={{ color: 'var(--text)' }}>Union presence</span>
              </label>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setStep(1)} className="btn btn-secondary">← Back</button>
              <button onClick={() => setStep(3)} className="btn btn-primary">Review →</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="card space-y-4">
            <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Confirm & Activate</h2>
            <div className="space-y-2 text-sm" style={{ color: 'var(--text)' }}>
              <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--border)' }}>
                <span style={{ color: 'var(--text-muted)' }}>Primary Jurisdiction</span>
                <strong>{form.primary_jurisdiction}</strong>
              </div>
              <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--border)' }}>
                <span style={{ color: 'var(--text-muted)' }}>Workplace Types</span>
                <strong>{form.workplace_types.join(', ') || 'Not set'}</strong>
              </div>
              <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--border)' }}>
                <span style={{ color: 'var(--text-muted)' }}>Safety Maturity</span>
                <strong className="capitalize">{form.safety_maturity}</strong>
              </div>
              <div className="flex justify-between py-2">
                <span style={{ color: 'var(--text-muted)' }}>Shift work / Union</span>
                <strong>{form.shift_work ? 'Shift work' : ''} {form.union_presence ? '/ Union' : ''} {!form.shift_work && !form.union_presence ? 'None' : ''}</strong>
              </div>
            </div>
            <div className="ai-label">
              Reviewed and approved — this configuration will be applied to your account
            </div>
            {saveError && <p className="text-sm" style={{ color: 'var(--danger)' }}>{saveError}</p>}
            <div className="flex gap-3">
              <button onClick={() => setStep(2)} className="btn btn-secondary">← Back</button>
              <button onClick={save} disabled={saving} className="btn btn-primary">
                {saving ? 'Saving...' : 'Activate HSEQ Intelligence →'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
